import { useState } from 'react'
import { Link, Navigate, useNavigate } from 'react-router-dom'
import { formatRut, isValidRut, normalizeRut, PORTAL_PIN_LENGTH } from '../utils/rut'
import { useSocioPortal } from '../contexts/SocioPortalContext'
import { Icon } from '../components/ui/Icons'
import InstallAppPrompt from '../components/pwa/InstallAppPrompt'

const GENERIC_ERROR = 'RUT o PIN incorrectos.'
const pinPattern = new RegExp(`^\\d{${PORTAL_PIN_LENGTH}}$`)

export default function SocioLoginPage() {
  const { signIn, isAuthenticated, loading } = useSocioPortal()
  const navigate = useNavigate()
  const [rut, setRut] = useState('')
  const [pin, setPin] = useState('')
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  if (isAuthenticated) return <Navigate to="/socio/inicio" replace />
  if (loading) return <div className="grid min-h-dvh place-items-center bg-background text-text-secondary" role="status">Cargando…</div>

  async function submit(event) {
    event.preventDefault()
    const normalizedRut = normalizeRut(rut)
    if (!isValidRut(normalizedRut) || !pinPattern.test(pin)) {
      setError(GENERIC_ERROR)
      return
    }
    setSubmitting(true)
    setError('')
    try {
      await signIn(normalizedRut, pin)
      navigate('/socio/inicio', { replace: true })
    } catch (submitError) {
      setError(submitError.message)
      setPin('')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <main className="flex min-h-dvh flex-col justify-center bg-background px-4 py-safe-page">
      <section className="mx-auto w-full max-w-md rounded-3xl border border-border bg-surface px-5 py-7 shadow-xl shadow-primary/10 sm:px-9 sm:py-8">
        <img src="/logo-cpa-transparent.png" alt="Centro de Padres y Apoderados CSPAH" width="625" height="777" className="mx-auto h-auto w-28 object-contain sm:w-32" />
        <p className="mt-6 text-center text-sm font-semibold uppercase tracking-widest text-primary">Portal del socio</p>
        <h1 className="mt-2 text-center text-2xl font-bold sm:text-3xl">Ingresa a tu credencial</h1>
        <p className="mt-3 text-center text-text-secondary">Usa tu RUT y un PIN formado por los primeros {PORTAL_PIN_LENGTH} números de tu RUT.</p>
        {error && <div className="mt-6 rounded-xl border border-danger/25 bg-danger/5 p-4 text-sm text-danger" role="alert">{error}</div>}
        <form onSubmit={submit} className="mt-8 space-y-5">
          <label className="block"><span className="mb-2 block text-sm font-semibold">RUT</span><input value={rut} onChange={(event) => setRut(event.target.value)} onBlur={() => setRut((value) => value ? formatRut(value) : '')} autoComplete="username" inputMode="text" autoCapitalize="characters" placeholder="12.345.678-5" required className="w-full rounded-xl border border-border px-4 py-3.5 text-lg outline-none transition focus:border-secondary focus:ring-4 focus:ring-secondary/15" /></label>
          <label className="block"><span className="mb-2 block text-sm font-semibold">PIN de {PORTAL_PIN_LENGTH} dígitos</span><input value={pin} onChange={(event) => setPin(event.target.value.replace(/\D/g, '').slice(0, PORTAL_PIN_LENGTH))} type="password" inputMode="numeric" autoComplete="current-password" placeholder="••••" minLength={PORTAL_PIN_LENGTH} maxLength={PORTAL_PIN_LENGTH} required className="w-full rounded-xl border border-border px-4 py-3.5 text-center text-2xl tracking-[0.45em] outline-none transition focus:border-secondary focus:ring-4 focus:ring-secondary/15" /></label>
          <button disabled={submitting} className="w-full rounded-xl bg-primary px-4 py-4 text-lg font-semibold text-white shadow-lg shadow-primary/20 transition hover:bg-primary-hover disabled:opacity-60">{submitting ? 'Ingresando…' : 'Ingresar'}</button>
        </form>
        <Link to="/ayuda" className="mt-6 flex min-h-12 items-center justify-center gap-2 rounded-xl border-2 border-success px-4 py-3 font-semibold text-success-text transition hover:bg-success/10"><Icon name="book" className="h-5 w-5" />Guía del Socio</Link>
        <InstallAppPrompt className="mt-8" />
        <Link to="/" className="mx-auto mt-4 flex min-h-11 w-fit items-center justify-center gap-1 px-4 text-sm font-semibold text-primary"><Icon name="chevronLeft" className="h-4 w-4" />Volver</Link>
      </section>
    </main>
  )
}
